import './App.css';
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import Logo from './components/Logo';
import Navigation from './components/Navigation';
import SearchBar from './components/SearchBar';
import Contact from './components/Contact';
import GuruGranthImage from './assets/guru-granth.png';
import LineDetails from './components/LineDetails';
import PageDisplay from './components/PageDisplay';
import TranslationFilters from './components/TranslationFilters';
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function Home({ showFilters, setShowFilters }) {
  const [query, setQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedLine, setSelectedLine] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [selectedTranslations, setSelectedTranslations] = useState([]);
  const [allResearchers, setAllResearchers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [pageResultsForVerse, setPageResultsForVerse] = useState(null);
  const [highlightedLine, setHighlightedLine] = useState(null);

  const performSearch = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setSelectedLine(null);
      setLoading(false);
      setError('');
      return;
    }

    setLoading(true);
    setError('');
    setSelectedLine(null);
    setPageResultsForVerse(null);
    setHighlightedLine(null);
    setSuggestions([]);

    try {
      let resp;
      const trimmedTerm = searchTerm.trim();
      
      // pure-number: page lookup
      if (/^\d+$/.test(trimmedTerm)) {
        const pageNum = parseInt(trimmedTerm);
        // Validate page number is between 1-1430
        if (pageNum < 1 || pageNum > 1430) {
          setSearchResults([]);
          setError('Please enter a page number between 1 - 1430 or search for a verse.');
          setLoading(false);
          return;
        }
        resp = await axios.get(`${API_BASE}/api/lines/page/${trimmedTerm}`);
        setSearchResults(resp.data);
      } else {
        // full-text search (verse search)
        resp = await axios.get(
          `${API_BASE}/api/lines/search?query=${encodeURIComponent(trimmedTerm)}`
        );
        // Check if no results found
        if (!resp.data || resp.data.length === 0) {
          setSearchResults([]);
          // Try to get suggestions for similar searches
          await fetchSuggestions(trimmedTerm);
          setError('Please enter a page number between 1 - 1430 or search for a verse.');
        } else {
          setSearchResults(resp.data);
          setSuggestions([]); // Clear suggestions when results are found
        }
      }
    } catch (err) {
      setSearchResults([]);
      // Check if it's a 404 or empty response for verse search
      if (err.response && err.response.status === 404) {
        // Try to get suggestions for similar searches
        const trimmedTerm = searchTerm.trim();
        if (!/^\d+$/.test(trimmedTerm)) {
          await fetchSuggestions(trimmedTerm);
        }
        setError('Please enter a page number between 1 - 1430 or search for a verse.');
      } else {
        setError('No results found or network error.');
        setSuggestions([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch search suggestions when no results found
  const fetchSuggestions = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      // Try searching with partial/fuzzy matching - try first few characters
      const partialQuery = searchTerm.slice(0, Math.max(2, Math.floor(searchTerm.length * 0.7)));
      const resp = await axios.get(
        `${API_BASE}/api/lines/search?query=${encodeURIComponent(partialQuery)}`
      );

      if (resp.data && resp.data.length > 0) {
        // Extract unique words/phrases from results for suggestions
        const uniqueSuggestions = new Set();
        resp.data.slice(0, 10).forEach(line => {
          // Extract words from line_text that contain the search term
          const words = line.line_text.split(/\s+/);
          words.forEach(word => {
            // Clean word and check if it's similar
            const cleanWord = word.replace(/[^\w\s]/g, '');
            if (cleanWord.length >= 2 && cleanWord.toLowerCase().includes(partialQuery.toLowerCase())) {
              uniqueSuggestions.add(cleanWord);
            }
          });
        });

        // Also try different variations
        const variations = [
          searchTerm.slice(0, -1), // Remove last character
          searchTerm + 'ਾ', // Add common matra
          searchTerm.slice(1), // Remove first character
        ];

        // Try each variation
        for (const variation of variations) {
          if (variation.length >= 2) {
            try {
              const varResp = await axios.get(
                `${API_BASE}/api/lines/search?query=${encodeURIComponent(variation)}`
              );
              if (varResp.data && varResp.data.length > 0) {
                uniqueSuggestions.add(variation);
              }
            } catch (e) {
              // Ignore errors for suggestions
            }
          }
        }

        setSuggestions(Array.from(uniqueSuggestions).slice(0, 5));
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      // If suggestion fetching fails, just don't show suggestions
      setSuggestions([]);
    }
  };

  const handleSearchChange = (newQuery) => {
    setQuery(newQuery);
    // Clear results when query is cleared
    if (!newQuery.trim()) {
      setSearchResults([]);
      setSelectedLine(null);
      setPageResultsForVerse(null);
      setHighlightedLine(null);
      setSearchQuery('');
      setError('');
    }
  };

  const handleFilterChange = (researcher) => {
    setSelectedTranslations(prevSelected =>
      prevSelected.includes(researcher)
        ? prevSelected.filter(name => name !== researcher)
        : [...prevSelected, researcher]
    );
  };

  const handleSearch = () => {
    setSearchQuery(query);
    performSearch(query);
  };

  const handlePageNavigation = (newPageNumber) => {
    const pageStr = String(newPageNumber);
    setQuery(pageStr);
    setSearchQuery(pageStr);
    performSearch(pageStr);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLineSelect = async (line) => {
    try {
      const resp = await axios.get(
        `${API_BASE}/api/lines/page/${line.page_number}`
      );
      // Set page results for display and highlight the selected line
      setPageResultsForVerse(resp.data);
      setHighlightedLine({
        page_number: line.page_number,
        verse_number: line.verse_number,
        line_number: line.line_number
      });
      // Clear selectedLine to show PageDisplay instead
      setSelectedLine(null);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error fetching page phrases:', err);
    }
  };

  const handleCloseDetails = () => {
    setSelectedLine(null);
  };

  // Clear highlight when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Don't clear if clicking on search result items (they will set a new highlight)
      const isClickOnSearchResult = e.target.closest('div[style*="padding: \'10px\'"]');
      
      if (!isClickOnSearchResult) {
        setHighlightedLine(null);
      }
    };

    if (highlightedLine) {
      // Use a small delay to allow search result clicks to process first
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [highlightedLine]);

  // Fetch all researchers when filters are shown and there are no search results
  useEffect(() => {
    const fetchAllResearchers = async () => {
      if (showFilters && searchResults.length === 0 && !selectedLine) {
        try {
          // Fetch page 1 to get all researchers
          const resp = await axios.get(`${API_BASE}/api/lines/page/1`);
          const allTranslations = resp.data.flatMap(v => v.translations || []);
          // Use Map to get unique researchers by name
          const researchersMap = new Map();
          allTranslations.forEach(t => {
            if (!researchersMap.has(t.researcher_name)) {
              researchersMap.set(t.researcher_name, {
                name: t.researcher_name,
                language: t.language
              });
            }
          });
          setAllResearchers(Array.from(researchersMap.values()));
        } catch (err) {
          console.error('Error fetching researchers:', err);
        }
      }
    };
    fetchAllResearchers();
  }, [showFilters, searchResults.length, selectedLine]);


  // Dynamic heading
  const renderHeading = () => {
    if (/^\d+$/.test(searchQuery.trim())) {
      return `Page ${searchQuery.trim()} Phrases:`;
    }
    return 'Matching Phrases:';
  };

  // Function to highlight search query in text
  const highlightSearchQuery = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const trimmedSearch = searchTerm.trim();
    if (!trimmedSearch) return text;
    
    // Escape special regex characters
    const escapedSearch = trimmedSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    // Create regex for case-insensitive matching (though Gurmukhi doesn't have cases)
    const regex = new RegExp(`(${escapedSearch})`, 'gi');
    
    // Split text by matches and wrap matches in highlight spans
    const parts = text.split(regex);
    
    return parts.map((part, index) => {
      // Check if this part matches the search term (case-insensitive)
      if (part.toLowerCase() === trimmedSearch.toLowerCase()) {
        return (
          <span key={index} style={{ color: 'orange', fontWeight: 'bold' }}>
            {part}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div>
      <h1>Gurbani Aarth Milan</h1>
      <h2 style={{ color: 'orange' }}>ਗੁਰਬਾਣੀ ਅਰਥ ਮਿਲਾਨ</h2>
      
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px', width: '100%', maxWidth: '600px', margin: '0 auto 10px auto' }}>
        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className="filters-button"
          title={showFilters ? 'Hide Translators' : 'Show Translators'}
        >
          {showFilters ? 'Hide Translators' : 'Show Translators'}
        </button>
      </div>

      <SearchBar 
        onSearchChange={handleSearchChange}
        onSearch={handleSearch}
        query={query}
        onKeyboardToggle={setKeyboardOpen}
      />

      {loading && <p>Loading...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      
      {/* Display suggestions when no results found */}
      {suggestions.length > 0 && (
        <div style={{ marginTop: '10px', maxWidth: '600px', margin: '10px auto' }}>
          <p style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
            Did you mean?
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setQuery(suggestion);
                  setSearchQuery(suggestion);
                  performSearch(suggestion);
                  setSuggestions([]);
                }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#f0f0f0',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Noto Sans Gurmukhi, Mukta, Gurmukhi, Segoe UI, system-ui, sans-serif',
                  transition: 'background-color 0.2s ease',
                  color: '#333'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#e0e0e0';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#f0f0f0';
                }}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 0, alignItems: 'flex-start' }} className="search-results-row">
        <div
          className={`search-results-content ${keyboardOpen ? 'keyboard-open' : ''}`}
          style={{
            flex: 1,
            marginTop: keyboardOpen ? '300px' : '10px',
            transition: 'margin-top 0.3s ease-in-out'
          }}
        >
          {selectedLine ? (
            <LineDetails
              line={selectedLine}
              onClose={handleCloseDetails}
              allVerses={selectedLine.allVerses || []}
              selectedTranslations={selectedTranslations}
              onFilterChange={handleFilterChange}
            />
          ) : (
            searchResults.length > 0 && (
              <div>
                {/^\d+$/.test(searchQuery.trim()) ? (
                  // Page search - show all results with filters
                  <PageDisplay 
                    pageResults={searchResults} 
                    pageNumber={searchQuery.trim()} 
                    selectedTranslations={selectedTranslations}
                    onFilterChange={handleFilterChange}
                    onPageNavigation={handlePageNavigation}
                    highlightedLine={highlightedLine}
                  />
                ) : pageResultsForVerse ? (
                  // Verse search - show page display with highlighted verse
                  <PageDisplay 
                    pageResults={pageResultsForVerse} 
                    pageNumber={pageResultsForVerse[0]?.page_number || ''} 
                    selectedTranslations={selectedTranslations}
                    onFilterChange={handleFilterChange}
                    onPageNavigation={handlePageNavigation}
                    highlightedLine={highlightedLine}
                  />
                ) : (
                  // Text search - show list of results (matching phrases)
                  <div className="matching-phrases" style={{ textAlign: 'left' }}>
                    <h3 style={{ textAlign: 'center' }}>{renderHeading()}</h3>
                    {searchResults.map((res, idx) => (
                      <div
                        key={idx}
                        title={`Page ${res.page_number} • Line ${res.line_number} • Verse ${res.verse_number}`}
                        style={{ padding: '4px 10px', cursor: 'pointer', textAlign: 'left' }}
                        onClick={() => handleLineSelect(res)}
                      >
                        <p style={{ textAlign: 'left', margin: 0 }}>
                          <span style={{ fontWeight: 'normal', fontSize: '0.5em' }}>(Page {res.page_number})</span>{' '}
                          {highlightSearchQuery(res.line_text, searchQuery)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          )}
        </div>

        {showFilters && (
          <div 
            className="translators-backdrop"
            onClick={() => setShowFilters(false)}
          />
        )}
        <div className="translators-container" style={{ 
          position: 'sticky',
          top: '20px',
          width: showFilters ? '250px' : '0',
          opacity: showFilters ? 1 : 0,
          overflow: 'hidden',
          marginTop: keyboardOpen ? '300px' : '20px',
          transition: 'width 0.3s ease-in-out, opacity 0.3s ease-in-out, margin-top 0.3s ease-in-out',
          pointerEvents: showFilters ? 'auto' : 'none'
        }}>
          {showFilters && (
            <TranslationFilters 
              translations={
                selectedLine 
                  ? (selectedLine.allVerses || []).flatMap(v => v.translations || [])
                  : searchResults.length > 0
                    ? searchResults.flatMap(v => v.translations || [])
                    : allResearchers.map(r => ({ researcher_name: r.name, language: r.language }))
              }
              selectedTranslations={selectedTranslations}
              onFilterChange={handleFilterChange}
              onClose={() => setShowFilters(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function About() {
  return (
    <div className="about-page">
      <h1>About</h1>
      <p className="about-text">
        The Gurbani Arth Milaan Project aims to create a digital collection by comparing and merging fifteen translations of the Sri Guru Granth Sahib—eight in Punjabi and seven in English. Since February 2021, a team of 33 research scholars has been working on this project, which is expected to be completed by the end of 2024.
      </p>
      <img src={GuruGranthImage} alt="Guru Granth Sahib" className="responsive-image" />
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const lastScrollY = useRef(0);
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    if (isNavOpen) setHeaderHidden(false);
  }, [isNavOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollBottom = documentHeight - (scrollTop + windowHeight);

      // Check if at bottom (within 50px)
      if (scrollBottom < 50) {
        setIsAtBottom(true);
        setShowScrollToTop(false);
      } else {
        setIsAtBottom(false);
        // Show return to top button when scrolled down more than 300px
        if (scrollTop > 300) {
          setShowScrollToTop(true);
        } else {
          setShowScrollToTop(false);
        }
      }

      // Mobile: hide header when scrolling down, show when scrolling up or near top
      if (isNavOpen) return;
      const threshold = 10;
      if (scrollTop < 50) {
        setHeaderHidden(false);
      } else if (scrollTop > lastScrollY.current + threshold) {
        setHeaderHidden(true);
      } else if (scrollTop < lastScrollY.current - threshold) {
        setHeaderHidden(false);
      }
      lastScrollY.current = scrollTop;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isNavOpen]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <header className={`header ${headerHidden ? 'header-scroll-hidden' : ''}`}>
        <Logo />
        <Navigation onMenuOpenChange={setIsNavOpen} />
      </header>
      <main>
        <Routes>
          <Route path="/" element={<Home showFilters={showFilters} setShowFilters={setShowFilters} />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
      </main>
      {showScrollToTop && !isAtBottom && (
        <div style={{ position: 'fixed', bottom: '20px', right: '20px', display: 'flex', flexDirection: 'column', gap: '10px', zIndex: 1000 }}>
          {isHomePage && (
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
              backgroundColor: '#ff8800',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 12px',
              fontSize: '11px',
              cursor: 'pointer',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.3s ease, transform 0.2s ease',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              minWidth: '120px'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#e67700';
              e.target.style.transform = 'scale(1.1)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#ff8800';
              e.target.style.transform = 'scale(1)';
            }}
            title={showFilters ? 'Hide Translators' : 'Show Translators'}
          >
            {showFilters ? 'Hide Translators' : 'Show Translators'}
          </button>
          )}
          <button
            onClick={scrollToTop}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              fontSize: '20px',
              cursor: 'pointer',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.3s ease, transform 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#0056b3';
              e.target.style.transform = 'scale(1.1)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#007bff';
              e.target.style.transform = 'scale(1)';
            }}
            title="Return to Top"
          >
            ↑
          </button>
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
